/**
 * TrendIndicator Component
 *
 * Displays a trending arrow with percentage change and tooltip context.
 * Used in stat cards to show week-over-week changes.
 *
 * Features:
 * - Up/down/neutral arrow icons with semantic colors
 * - Percentage value display
 * - Tooltip showing comparison period
 * - Accessible with proper ARIA labels
 */

import { Icon, InlineStack, Text, Tooltip } from '@shopify/polaris';
import { ArrowUpIcon, ArrowDownIcon, MinusIcon } from '@shopify/polaris-icons';

export interface TrendData {
  /** Percentage change value (e.g., 15 = +15%, -10 = -10%) */
  value: number;
  /** Direction of the trend */
  direction: 'up' | 'down' | 'neutral';
  /** Period description for tooltip (e.g., "vs. last week") */
  period: string;
}

export interface TrendIndicatorProps extends TrendData {
  /** Override the default tone (up=success, down=critical) */
  invertTone?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
}

/**
 * TrendIndicator - Shows trend direction with percentage change
 */
export function TrendIndicator({
  value,
  direction,
  period,
  invertTone = false,
  size = 'small',
}: TrendIndicatorProps) {
  // Determine icon based on direction
  const iconSource = direction === 'up' 
    ? ArrowUpIcon 
    : direction === 'down' 
      ? ArrowDownIcon 
      : MinusIcon;

  // Determine tone - can be inverted for metrics where down is good (e.g., critical alerts)
  const getTone = (): 'success' | 'critical' | 'subdued' => {
    if (direction === 'neutral') return 'subdued';
    
    const isPositive = direction === 'up';
    const effectivePositive = invertTone ? !isPositive : isPositive;
    
    return effectivePositive ? 'success' : 'critical';
  };

  const tone = getTone();
  const absValue = Math.abs(value);
  const displayValue = absValue < 1 && absValue > 0 
    ? '<1' 
    : Math.round(absValue).toString();

  // Size-specific styles
  const fontSize = size === 'small' ? '11px' : '12px';
  const iconScale = size === 'small' ? 0.85 : 1;

  // Build accessible label
  const ariaLabel = `${direction === 'up' ? 'Increased' : direction === 'down' ? 'Decreased' : 'No change'} by ${displayValue}% ${period}`;

  const content = (
    <div
      className={`trend-indicator trend-indicator--${tone} trend-indicator--${size}`}
      role="status"
      aria-label={ariaLabel}
    >
      <InlineStack gap="050" blockAlign="center" wrap={false}>
        <span style={{ transform: `scale(${iconScale})`, display: 'flex' }}>
          <Icon source={iconSource} tone={tone} />
        </span>
        <Text as="span" variant="bodySm" tone={tone}>
          <span style={{ fontSize, fontWeight: 600 }}>
            {displayValue}%
          </span>
        </Text>
      </InlineStack>
    </div>
  );

  return (
    <Tooltip content={period} preferredPosition="above">
      {content}
    </Tooltip>
  );
}

export default TrendIndicator;
